import { useRef, useState } from 'react';
import { Box, BoxProps } from '@chakra-ui/react';
import useInterval from 'use-interval';

import asBentoPage from 'common/bento/hoc/asBentoPage/asBentoPage';
import webView from 'common/webView';
import Heading from 'components/_core/Heading';
import { PageLayout } from 'features/Bento/components';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import loadScript from './libs/loadScript';
import locales from './locales';

const SASU_CREATION_BOT_URL =
  'https://storage.googleapis.com/landbot.pro/v3/H-1744709-JLWX7R5HCOTKGIDQ/index.json';

const landbotScriptAttributes = {
  SameSite: 'None; Secure',
  src: 'https://static.landbot.io/landbot-3/landbot-3.0.0.js',
  type: 'text/javascript',
};

interface SasuLandbotCustomData {
  company_profile_id: string;
  email: string;
  first_name: string;
  last_name: string;
  nom_epouse: string;
  phone: string;
  prenom_principal: string;
  uid: string;
  environment: 'production' | 'staging';
}

interface SasuCreationLandbotProps extends BoxProps {
  customData: SasuLandbotCustomData;
  callback?: (event: LandbotCallbackPayload) => void;
}

type LandbotCallbackPayload = 'done' | 'abort' | 'redirected_to_partner';

interface SasuCreationLandbotProps {
  customData: SasuLandbotCustomData;
  onDone: () => void;
  onRedirectToPartner: () => void;
}

/**
 * The component that embeds the signup landbot in the page.
 * https://static.landbot.io/landbot-3/storybook-docs/index.html?path=/story/advanced-embedding-in-your-app--page
 */
const SasuCreationLandbot = asBentoPage<SasuCreationLandbotProps>(
  ({ customData, Layout, onDone, onPrev, onRedirectToPartner }) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const [isLandbotLoaded, setIsLandbotLoaded] = useState(false);

    loadScript(landbotScriptAttributes);

    useInterval(
      () => {
        if (window.Landbot) {
          setIsLandbotLoaded(true);

          const myLandbot = new window.Landbot.Container({
            configUrl: SASU_CREATION_BOT_URL,
            container: containerRef.current,
            customData,
          });

          myLandbot.onLoad(() => {
            myLandbot.window.callback = (event: LandbotCallbackPayload) => {
              if (event === 'done') {
                onDone();
              } else if (event === 'redirected_to_partner') {
                onRedirectToPartner();
              } else {
                onPrev();
              }
            };
          });
        }
      },
      isLandbotLoaded ? null : 10,
    );

    return (
      <Layout
        nav={webView.config?.isEmbedInAppLayout ? null : <OBNavigation />}
      >
        <Box display="flex" flexDirection="column" height="100%">
          <PageLayout.Header marginBottom="space-24">
            <Heading size="lg">{locales.title}</Heading>
          </PageLayout.Header>
          <Box
            display="flex"
            flex={1}
            flexDirection="column"
            ref={containerRef}
            // This a trick to inject CSS styles to the container and iframe that are created by Landbot library.
            // The main goal is to fulfill the whole space of the container without having to set a fixed height.
            // We don't have access to the iframe's inline style directly, so we have to use the CSS selector to target it.
            sx={{
              '> div': {
                '> iframe': {
                  height: 'auto !important',
                  width: '100%',
                },
                display: 'flex',
                flex: '1',
                height: 'initial',
                width: '100%',
              },
            }}
          />
        </Box>
      </Layout>
    );
  },
);

export default SasuCreationLandbot;
